import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DatenschutzText, UnterschriftWeg, UnterweisungPublic } from '../../../services/sifa/types';
import {
  fehlerCode,
  ladeUnterweisungPublic,
  starteTeilnahme,
  schliesseTeilnahmeAb,
} from '../../../services/sifa/sifaPublicService';
import { TestKontingentErschoepftError, ladeTestPublic, werteTestAus } from '../../../services/sifa/testlinkService';
import { ladeDatenschutzPublic } from '../../../services/sifa/datenschutzService';
import { describeError } from '../../../services/errorMessage';
import {
  antwortUmschalten,
  ergebnisVorhanden,
  ersterSchrittDerArt,
  parseZustand,
  schrittBegrenzen,
  schrittfolge,
  serialisiereZustand,
  sessionKey,
  startZustand,
  testErgebnisAus,
  type Schritt,
  type TeilnahmeFormular,
  type TeilnahmeModus,
  type TeilnahmeZustand,
} from './teilnahmeSeiteLogic';
import {
  DATENSCHUTZ_AKTUALISIERT,
  abschlussFrei,
  baueBestaetigung,
  datenschutzVorAbschluss,
  istKontingentErschoepft,
  type DatenschutzNeu,
  type DatenschutzStand,
} from './abschlussLogic';

/**
 * Zustand und Handlungen der oeffentlichen Teilnehmerseite.
 *
 * Alles, was ein Neuladen ueberleben muss (Sitzungstoken, Schritt, Antworten,
 * Formular, Ergebnis), liegt im sessionStorage je linkToken — die
 * Unterweisung selbst wird bei jedem Laden frisch ueber die anon-RPC geholt,
 * damit ein zwischenzeitlich deaktivierter Link auch wirklich zu ist.
 *
 * Kein Login, keine Tabelle: nur die RPCs aus sifaPublicService.
 *
 * Testlauf (`modus = 'test'`, #/unterweisung-test/<token>): Kurs aus
 * ladeTestPublic, KEIN starteTeilnahme, Abschluss nur ueber werteTestAus —
 * es entsteht keine Zeile in der Datenbank. Eigener sessionStorage-Schluessel.
 */

export type LadeStatus = 'laedt' | 'bereit' | 'ungueltig' | 'fehler';

/** Stand des Zertifikat-Versands (POST /api/unterweisung-zertifikat). */
export type ZertifikatStatus = 'offen' | 'laeuft' | 'gesendet' | 'folgt';

const leseGespeichert = (linkToken: string, modus: TeilnahmeModus): TeilnahmeZustand => {
  try {
    return parseZustand(sessionStorage.getItem(sessionKey(linkToken, modus)), modus) ?? startZustand(modus);
  } catch {
    // Privater Modus / gesperrter Speicher: die Seite laeuft ohne Persistenz weiter.
    return startZustand(modus);
  }
};

const vergiss = (linkToken: string, modus: TeilnahmeModus): void => {
  try {
    sessionStorage.removeItem(sessionKey(linkToken, modus));
  } catch {
    // s. u.
  }
};

/**
 * Meldungen zu den SQLSTATEs der RPCs. `P0002` deckt alles ab, was die
 * Sitzung unbrauchbar macht (abgelaufen, unbekannt, Link inaktiv, zu viele
 * Fehlversuche) — die Antwort darauf ist immer dieselbe: neu beginnen.
 */
const SITZUNG_VERLOREN =
  'Diese Sitzung ist nicht mehr gültig — abgelaufen, bereits abgeschlossen oder zu viele Fehlversuche. Bitte die Unterweisung neu beginnen.';
const ZU_VIELE_ANLAEUFE =
  'Gerade sind zu viele Durchführungen gleichzeitig offen. Bitte in ein paar Minuten erneut versuchen.';
const KEINE_HAEKCHEN = 'Bitte zuerst beide Häkchen setzen — danach kannst du unterschreiben.';

const speichere = (linkToken: string, modus: TeilnahmeModus, zustand: TeilnahmeZustand): void => {
  try {
    sessionStorage.setItem(sessionKey(linkToken, modus), serialisiereZustand(zustand));
  } catch {
    // s. o. — ein voller oder gesperrter Speicher darf die Teilnahme nicht stoppen.
  }
};

export const useTeilnahme = (linkToken: string, modus: TeilnahmeModus = 'echt') => {
  const test = modus === 'test';
  const [ladeStatus, setLadeStatus] = useState<LadeStatus>('laedt');
  const [ladeFehler, setLadeFehler] = useState('');
  const [unterweisung, setUnterweisung] = useState<UnterweisungPublic | null>(null);
  /** Nur im Testlauf: Ablauf des Test-Links (ISO). */
  const [testGueltigBis, setTestGueltigBis] = useState<string | null>(null);
  const [zustand, setZustand] = useState<TeilnahmeZustand>(() => leseGespeichert(linkToken, modus));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [zertifikatStatus, setZertifikatStatus] = useState<ZertifikatStatus>(() =>
    leseGespeichert(linkToken, modus).zertifikatAngefordert ? 'gesendet' : 'offen',
  );
  /** Nach einem Sitzungsverlust: die Begruessung sagt warum und bietet „Neu beginnen". */
  const [neustartGrund, setNeustartGrund] = useState<string | null>(null);
  // Die Haekchen liegen bewusst NICHT im sessionStorage: bestaetigt wird
  // unmittelbar vor der Unterschrift, nach einem Neuladen also erneut.
  const [bestaetigt, setBestaetigtRoh] = useState(false);
  const [datenschutzGelesen, setDatenschutzGelesenRoh] = useState(false);
  /** Das Unterschriftsfeld ist offen — Overlay am PC, Vollbild am Handy (nur mit beiden Haekchen). */
  const [unterschriftOffen, setUnterschriftOffen] = useState(false);
  const [datenschutz, setDatenschutz] = useState<DatenschutzStand>({ stand: 'laedt', text: null });
  /** Testlauf: das Auswertungs-Kontingent des Links ist aufgebraucht (statt Ergebnis ein Klartext). */
  const [testErschoepft, setTestErschoepft] = useState(false);
  // Riegel gegen den doppelten Versand-Aufruf (StrictMode rendert Effekte zweimal).
  const zertifikatLaeuft = useRef(false);

  useEffect(() => {
    let aktiv = true;
    setLadeStatus('laedt');
    const laden: Promise<UnterweisungPublic | null> = test
      ? ladeTestPublic(linkToken).then((u) => {
          if (aktiv) setTestGueltigBis(u?.gueltigBis || null);
          return u;
        })
      : ladeUnterweisungPublic(linkToken);
    laden
      .then((u) => {
        if (!aktiv) return;
        if (!u) {
          setLadeStatus('ungueltig');
          return;
        }
        setUnterweisung(u);
        setLadeStatus('bereit');
      })
      .catch((e) => {
        if (!aktiv) return;
        setLadeFehler(describeError(e));
        setLadeStatus('fehler');
      });
    return () => {
      aktiv = false;
    };
  }, [linkToken, test]);

  // Die Fassung der Datenschutzhinweise schon beim Laden holen: sie gehoert zu
  // dem, was der Teilnehmer bestaetigt. Im Testlauf wird nichts gespeichert,
  // dort braucht sie niemand (die Datenschutzseite laedt selbst).
  useEffect(() => {
    if (test) return undefined;
    let aktiv = true;
    ladeDatenschutzPublic({ art: 'link', token: linkToken })
      .then((text) => {
        if (aktiv) setDatenschutz({ stand: 'bereit', text });
      })
      .catch(() => {
        if (aktiv) setDatenschutz({ stand: 'fehler', text: null });
      });
    return () => {
      aktiv = false;
    };
  }, [linkToken, test]);

  useEffect(() => {
    speichere(linkToken, modus, zustand);
  }, [linkToken, modus, zustand]);

  const folge = useMemo<Schritt[]>(
    () => schrittfolge(unterweisung ?? { inhalt: [], fragen: [] }),
    [unterweisung],
  );

  // Ein Ergebnis-Schritt ohne Ergebnis (z. B. gekuerzter Kurs nach Neuladen)
  // faellt auf die Unterschrift zurueck, sonst zeigte die Seite eine leere Karte.
  const schrittIndex = useMemo(() => {
    const i = schrittBegrenzen(folge, zustand.schritt);
    if (folge[i]?.art === 'ergebnis' && !ergebnisVorhanden(zustand, modus)) return Math.max(0, i - 1);
    return i;
  }, [folge, zustand, modus]);
  const schritt: Schritt = folge[schrittIndex] ?? { art: 'begruessung', index: 0 };

  const haekchenZuruecksetzen = useCallback(() => {
    setBestaetigtRoh(false);
    setDatenschutzGelesenRoh(false);
    setUnterschriftOffen(false);
  }, []);

  /** Wer ein Haekchen wieder wegnimmt, kann nicht unterschreiben — auch das Vollbild schliesst. */
  const setBestaetigt = useCallback((wert: boolean) => {
    setBestaetigtRoh(wert);
    if (!wert) setUnterschriftOffen(false);
  }, []);
  const setDatenschutzGelesen = useCallback((wert: boolean) => {
    setDatenschutzGelesenRoh(wert);
    if (!wert) setUnterschriftOffen(false);
  }, []);
  const frei = abschlussFrei({ bestaetigt, datenschutzGelesen });

  /** Unterschriftsfeld oeffnen — nur mit beiden Haekchen. */
  const unterschriftOeffnen = useCallback(() => {
    if (!frei) {
      setFehler(KEINE_HAEKCHEN);
      return;
    }
    setFehler(null);
    setUnterschriftOffen(true);
  }, [frei]);
  const unterschriftSchliessen = useCallback(() => setUnterschriftOffen(false), []);

  const gehe = useCallback((index: number) => {
    setFehler(null);
    setUnterschriftOffen(false);
    setZustand((z) => ({ ...z, schritt: index }));
  }, []);

  /**
   * Alles auf Anfang: Sitzung, Antworten, Formular, Ergebnis — und der
   * sessionStorage-Eintrag. Keine Sackgasse: wer hier landet, kann sofort
   * wieder ueber die Begruessung starten (neuer Token beim Beginnen).
   */
  const neuBeginnen = useCallback((grund: string | null = null) => {
    vergiss(linkToken, modus);
    zertifikatLaeuft.current = false;
    setZertifikatStatus('offen');
    setFehler(null);
    setNeustartGrund(grund);
    setTestErschoepft(false);
    haekchenZuruecksetzen();
    setZustand(startZustand(modus));
  }, [haekchenZuruecksetzen, linkToken, modus]);

  /** Fehler einer RPC einordnen: Sitzung verloren → Neustart; sonst Klartext anzeigen. */
  const behandleFehler = useCallback((e: unknown) => {
    const code = fehlerCode(e);
    if (code === 'P0002') {
      neuBeginnen(SITZUNG_VERLOREN);
      return;
    }
    setFehler(code === '54000' ? ZU_VIELE_ANLAEUFE : describeError(e));
  }, [neuBeginnen]);

  /** „Unterweisung beginnen": legt die Durchfuehrung an und geht zum ersten Inhalt. */
  const beginnen = useCallback(async () => {
    if (test) {
      // Testlauf: keine Durchfuehrung in der Datenbank — einfach los.
      setFehler(null);
      setNeustartGrund(null);
      haekchenZuruecksetzen();
      setZustand({ ...startZustand('test'), schritt: 1 });
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      const token = await starteTeilnahme(linkToken);
      setNeustartGrund(null);
      haekchenZuruecksetzen();
      setZustand({ ...startZustand('echt'), teilnahmeToken: token, schritt: 1 });
    } catch (e) {
      // 54000 = Massenanlage-Riegel der RPC; P0002 = Link inaktiv. Beides
      // ist hier kein Sitzungsverlust (es gibt noch keine), nur eine Meldung.
      const code = fehlerCode(e);
      setFehler(code === '54000' ? ZU_VIELE_ANLAEUFE : describeError(e));
    } finally {
      setBusy(false);
    }
  }, [haekchenZuruecksetzen, linkToken, test]);

  const weiter = useCallback(() => {
    gehe(Math.min(folge.length - 1, schrittIndex + 1));
  }, [folge.length, gehe, schrittIndex]);

  /** Zurueck bis zum ersten Inhalt — vor die Begruessung geht es nach dem Start nicht mehr. */
  const zurueck = useCallback(() => {
    gehe(Math.max(1, schrittIndex - 1));
  }, [gehe, schrittIndex]);

  const antwortWechseln = useCallback((frageId: string, antwortId: string) => {
    setZustand((z) => ({ ...z, antworten: antwortUmschalten(z.antworten, frageId, antwortId) }));
  }, []);

  const formularAendern = useCallback((feld: keyof TeilnahmeFormular, wert: string) => {
    setZustand((z) => ({ ...z, formular: { ...z.formular, [feld]: wert } }));
  }, []);

  const verstandenSetzen = useCallback((wert: boolean) => {
    setZustand((z) => ({ ...z, verstanden: wert }));
  }, []);

  /**
   * Die Fassung der Datenschutzhinweise UNMITTELBAR vor dem Abschluss neu
   * holen: der Link zeigt immer die neueste, und die bestaetigte muss die
   * sein, die der Teilnehmer lesen konnte. Die Entscheidung trifft
   * datenschutzVorAbschluss — neue Fassung seit dem Einstieg → neu bestaetigen
   * lassen; Ladefehler → mit der zuletzt geladenen weiter (fail-open, im
   * schlimmsten Fall das Muster): wer alles gemacht hat, wird nicht an einer
   * Nebenabfrage abgewiesen.
   */
  const datenschutzFuerAbschluss = useCallback(async (): Promise<{ weiter: boolean; text: DatenschutzText | null }> => {
    let neu: DatenschutzNeu;
    try {
      neu = { ok: true, text: await ladeDatenschutzPublic({ art: 'link', token: linkToken }) };
    } catch {
      neu = { ok: false };
    }
    const entscheid = datenschutzVorAbschluss(datenschutz, neu);
    if (neu.ok) setDatenschutz({ stand: 'bereit', text: neu.text });
    return entscheid;
  }, [datenschutz, linkToken]);

  /**
   * Abschluss mit Unterschrift. Auswertung, Mitarbeiter-Abgleich und
   * Zertifikat-Nr. passieren serverseitig; der Client bekommt nur das Ergebnis.
   * Die Bestaetigung (Wortlaut + Fassung der Datenschutzhinweise) baut
   * baueBestaetigung — derselbe Satz, den die Seite am Haekchen anzeigt.
   *
   * Testlauf: die Unterschrift wird verworfen, werteTestAus rechnet nur.
   */
  const abschliessen = useCallback(
    async (unterschrift: string | null, weg: UnterschriftWeg) => {
      // Auch die QR-Bruecke kommt hier durch — ohne Haekchen kein Abschluss.
      if (!frei) {
        setFehler(KEINE_HAEKCHEN);
        return;
      }
      if (test) {
        setBusy(true);
        setFehler(null);
        try {
          // Nur die angezeigten Felder in den Zustand (testErgebnisAus) — nichts, was Antworten verraten koennte.
          const testErgebnis = testErgebnisAus(await werteTestAus(linkToken, zustand.antworten));
          const zielIndex = ersterSchrittDerArt(folge, 'ergebnis');
          setUnterschriftOffen(false);
          setZustand((z) => ({ ...z, testErgebnis, schritt: zielIndex >= 0 ? zielIndex : z.schritt }));
        } catch (e) {
          // Klasse aus dem Dienst; Name/Code als Rueckfall, falls der Fehler anders verpackt ankommt.
          if (e instanceof TestKontingentErschoepftError || istKontingentErschoepft(e)) {
            setUnterschriftOffen(false);
            setTestErschoepft(true);
          } else {
            setFehler(describeError(e));
          }
        } finally {
          setBusy(false);
        }
        return;
      }
      const token = zustand.teilnahmeToken;
      if (!token || !unterweisung) {
        setFehler('Die Sitzung ist verloren gegangen. Bitte die Unterweisung neu beginnen.');
        return;
      }
      setBusy(true);
      setFehler(null);
      try {
        const datenschutzJetzt = await datenschutzFuerAbschluss();
        if (!datenschutzJetzt.weiter) {
          // Auch der QR-Weg landet hier: die Unterschrift vom Handy wird nicht
          // verwendet, nach dem neuen Haekchen wird neu unterschrieben.
          setDatenschutzGelesen(false);
          setFehler(DATENSCHUTZ_AKTUALISIERT);
          return;
        }
        const bestaetigung = baueBestaetigung(unterweisung.titel, datenschutzJetzt.text);
        const ergebnis = await schliesseTeilnahmeAb(token, zustand.formular, zustand.antworten, unterschrift, weg, bestaetigung);
        const zielIndex = ersterSchrittDerArt(folge, 'ergebnis');
        setUnterschriftOffen(false);
        setZustand((z) => ({ ...z, ergebnis, schritt: zielIndex >= 0 ? zielIndex : z.schritt }));
      } catch (e) {
        behandleFehler(e);
      } finally {
        setBusy(false);
      }
    },
    [
      behandleFehler,
      datenschutzFuerAbschluss,
      folge,
      frei,
      linkToken,
      setDatenschutzGelesen,
      test,
      unterweisung,
      zustand.antworten,
      zustand.formular,
      zustand.teilnahmeToken,
    ],
  );

  /**
   * Nach „nicht bestanden": derselbe Sitzungstoken laeuft weiter. Die RPC
   * `sifa_teilnahme_abschliessen` (Nr. 83) setzt `abgeschlossen_am` nur bei
   * BESTANDEN; ein gescheiterter Versuch schreibt Antworten und Punkte in
   * dieselbe Zeile und laesst den naechsten Aufruf zu. So steht je Person und
   * Durchfuehrung EINE Zeile — ein frischer Token je Anlauf liesse fuer jede
   * nicht bestandene Runde eine verwaiste Teilnahme zurueck. Nur wenn kein
   * Token mehr da ist (Sitzung verloren), wird ein neuer geholt. Antworten
   * werden geleert, die Formulareingaben bleiben (dieselbe Person). Die
   * Haekchen fallen zurueck: bestaetigt wird jeder Abschluss neu.
   */
  const neuerAnlauf = useCallback(
    async (ziel: 'fragen' | 'inhalte') => {
      setBusy(true);
      setFehler(null);
      haekchenZuruecksetzen();
      try {
        // Im Testlauf gibt es keinen Token, und es wird auch keiner geholt.
        const token = test ? null : (zustand.teilnahmeToken ?? (await starteTeilnahme(linkToken)));
        const fragenIndex = ersterSchrittDerArt(folge, 'frage');
        const inhalteIndex = ersterSchrittDerArt(folge, 'abschnitt');
        const formularIndex = ersterSchrittDerArt(folge, 'formular');
        const zielIndex =
          ziel === 'inhalte' && inhalteIndex >= 0
            ? inhalteIndex
            : fragenIndex >= 0
              ? fragenIndex
              : formularIndex;
        zertifikatLaeuft.current = false;
        setZertifikatStatus('offen');
        setZustand((z) => ({
          ...z,
          teilnahmeToken: token,
          antworten: [],
          ergebnis: null,
          ...(test ? { testErgebnis: null } : {}),
          zertifikatAngefordert: false,
          schritt: Math.max(1, zielIndex),
        }));
      } catch (e) {
        behandleFehler(e);
      } finally {
        setBusy(false);
      }
    },
    [behandleFehler, folge, haekchenZuruecksetzen, linkToken, test, zustand.teilnahmeToken],
  );

  const nochmalVersuchen = useCallback(() => neuerAnlauf('fragen'), [neuerAnlauf]);
  const inhalteNochmalLesen = useCallback(() => neuerAnlauf('inhalte'), [neuerAnlauf]);

  /**
   * Stoesst den Zertifikat-Versand an. Fehler blockieren nie — der Server
   * haelt den Versand in der Warteschlange, der Innendienst kann ihn im Modul
   * ausloesen. 202 = angenommen, aber noch nicht gesendet („Versand folgt").
   */
  const zertifikatAnfordern = useCallback(async () => {
    const token = zustand.teilnahmeToken;
    // Testlauf: nie ein Zertifikat, nie eine E-Mail.
    if (test || !token || zustand.zertifikatAngefordert || zertifikatLaeuft.current) return;
    zertifikatLaeuft.current = true;
    setZertifikatStatus('laeuft');
    try {
      const antwort = await fetch('/api/unterweisung-zertifikat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teilnahmeToken: token }),
      });
      if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
      setZertifikatStatus(antwort.status === 202 ? 'folgt' : 'gesendet');
      setZustand((z) => ({ ...z, zertifikatAngefordert: true }));
    } catch {
      setZertifikatStatus('folgt');
      zertifikatLaeuft.current = false;
    }
  }, [test, zustand.teilnahmeToken, zustand.zertifikatAngefordert]);

  return {
    modus,
    ladeStatus,
    ladeFehler,
    unterweisung,
    testGueltigBis,
    zustand,
    testErschoepft,
    folge,
    schrittIndex,
    schritt,
    busy,
    fehler,
    neustartGrund,
    zertifikatStatus,
    beginnen,
    neuBeginnen,
    weiter,
    zurueck,
    antwortWechseln,
    formularAendern,
    verstandenSetzen,
    bestaetigt,
    datenschutzGelesen,
    abschlussFrei: frei,
    setBestaetigt,
    setDatenschutzGelesen,
    unterschriftOffen,
    unterschriftOeffnen,
    unterschriftSchliessen,
    abschliessen,
    nochmalVersuchen,
    inhalteNochmalLesen,
    zertifikatAnfordern,
  };
};

export type TeilnahmeSteuerung = ReturnType<typeof useTeilnahme>;
